import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { apiV2 } from "api/firebase";

import {
  IChangeAbilityExhibitorsToSendProfileRequestReq,
  IGetAllShowQueryReq,
  IGetOneShowQueryReq,
  IGetShowByOrganiserUserQueryReq,
  IGetShowByOwnerQueryReq,
  IGetShowBySupplierCompanyQueryReq,
  IGetShowBySupplierQueryReq,
  IUpdateShowReq,
} from "@evennt-tech/api-v2-external";
import {
  INotificationBaseRelatedToShow,
  IPayloadUpdatedShow,
  IShow,
  NotificationType,
  Roles,
} from "@evennt-tech/api-v2-core";
import { getErrorMessage } from "tools/error";
import { registerStore } from "store/utils/reset-store.utils";
import { getRegisteredStore } from "store/store-registry";
import { SHOWS_PATH } from "view/routes";

const isSupplier = (): boolean => {
  try {
    return getRegisteredStore().getState().auth?.role === Roles.SUPPLIER;
  } catch {
    return false;
  }
};

type State = {
  items: IShow[];
  itemsByOwnerId: IShow[];
  itemsByOrganiserUser: IShow[];
  itemsBySupplier: IShow[];
  itemsBySupplierCompany: IShow[];
  selected: IShow | null;
  isLoading: boolean;
  isLoadingOne: boolean;
  error: string | null;
};

type Action = {
  fetchByOwner: (params: IGetShowByOwnerQueryReq) => Promise<void>;
  fetchAll: (params: IGetAllShowQueryReq) => Promise<void>;
  fetchRelatedShowByOrganiserUser: (
    params: IGetShowByOrganiserUserQueryReq,
    force?: boolean
  ) => Promise<void>;
  changeAbilityExhibitorsToRequestProfileChanges: (
    params: IChangeAbilityExhibitorsToSendProfileRequestReq
  ) => Promise<void>;
  fetchOne: (params: IGetOneShowQueryReq, force?: boolean) => Promise<void>;
  fetchBySupplier: (params: IGetShowBySupplierQueryReq) => Promise<void>;
  fetchBySupplierCompany: (
    params: IGetShowBySupplierCompanyQueryReq
  ) => Promise<void>;
  autoCatchByOrganiser: (
    params: IGetShowByOrganiserUserQueryReq
  ) => Promise<void>;
  autoCatchBySupplier: (params: IGetShowBySupplierQueryReq) => Promise<void>;
  autoCatchOne: (showId: string) => () => void;
  resetOne: () => void;
  update: (body: IUpdateShowReq) => Promise<void>;
};

// Module-level dedup/freshness bookkeeping for the by-organiser-user
// fetch. `useOrganiserShows` is mounted by many components and fired
// `fetchRelatedShowByOrganiserUser({})` on every mount with no guard, so
// the endpoint was hit once per consumer per navigation. In-flight dedup
// collapses concurrent mounts to one request; the freshness window skips
// redundant refetches between navigations. Notification-driven refreshes
// (CREATED_SHOW / UPDATED_SHOW) pass `force` to bypass both.
let byOrganiserUserInFlight: Promise<void> | null = null;
let byOrganiserUserFetchedAt = 0;
const BY_ORGANISER_USER_FRESH_MS = 5 * 60_000;

export const useShows = create<State & Action>()(
  devtools((set, get) => ({
    items: [],
    itemsByOwnerId: [],
    itemsByOrganiserUser: [],
    itemsBySupplier: [],
    itemsBySupplierCompany: [],
    selected: null,
    isLoading: false,
    isLoadingOne: false,
    error: null,

    fetchByOwner: async (params) => {
      try {
        const { organiserId } = params;
        const showsByOwner = await apiV2.show.getByOwner({
          organiserId,
        });
        set({ itemsByOwnerId: showsByOwner });
      } catch (e) {
        console.error("Failed to fetch shows by owner:", e);
        set({ itemsByOwnerId: [] });
      }
    },

    changeAbilityExhibitorsToRequestProfileChanges: async (params) => {
      const prev = get().selected;
      if (prev) {
        set({
          selected: {
            ...prev,
            isExhibitorAbleToRequestProfileChanges: params.can,
          },
        });
      }
      try {
        await apiV2.show.changeAbilityExhibitorsToRequestProfileChanges(params);
      } catch (e) {
        if (prev) {
          set({ selected: prev });
        }
        throw e;
      }
    },

    fetchOne: async (params, force = false) => {
      try {
        if (!force) {
          if (get().isLoadingOne || get().selected) {
            return;
          }
        }

        set({
          isLoadingOne: true,
        });

        const data = await apiV2.show.getOne(params);

        if (!data) window.location.replace(SHOWS_PATH);

        set({ selected: data, isLoadingOne: false });
      } catch (e) {
        console.error("Failed to fetch show:", e);
        set({ selected: null, isLoadingOne: false });
      }
    },

    fetchAll: async (params) => {
      try {
        const data = await apiV2.show.getAll(params);
        set({ items: data });
      } catch (e) {
        console.error("Failed to fetch all show:", e);
        set({ items: [] });
      }
    },

    autoCatchByOrganiser: async (params) => {
      const { fetchRelatedShowByOrganiserUser } = get();
      const keys = [NotificationType.CREATED_SHOW];

      for (const key of keys) {
        apiV2.notification.subscribe({
          key,
          listenerId: `show_by_organiser_store_${key}`,
          callback: () => fetchRelatedShowByOrganiserUser(params, true),
        });
      }
    },

    fetchRelatedShowByOrganiserUser: async (_params, force = false) => {
      // Collapse concurrent mounts onto one in-flight request.
      if (byOrganiserUserInFlight) return byOrganiserUserInFlight;
      // Skip redundant refetches within the freshness window unless forced
      // (notification-driven refreshes pass `force`).
      if (
        !force &&
        byOrganiserUserFetchedAt &&
        Date.now() - byOrganiserUserFetchedAt < BY_ORGANISER_USER_FRESH_MS
      ) {
        return undefined;
      }

      byOrganiserUserInFlight = (async () => {
        try {
          const showsByOrganiser = await apiV2.show.getRelatedShowByOrganiserUser(
            {}
          );
          set({ itemsByOrganiserUser: showsByOrganiser });
          byOrganiserUserFetchedAt = Date.now();
        } catch (e) {
          console.error("Failed to fetch shows by organiser user:", e);
          set({ itemsByOrganiserUser: [] });
        } finally {
          byOrganiserUserInFlight = null;
        }
      })();

      return byOrganiserUserInFlight;
    },

    autoCatchBySupplier: async (params) => {
      const { fetchBySupplier } = get();
      const keys = [
        NotificationType.ASSIGN_SUPPLIER_COMPANY_TO_SHOW,
        NotificationType.EDIT_SUPPLIER_COMPANY_TO_SHOW,
        NotificationType.DELETE_SUPPLIER_COMPANY_FROM_SHOW,
        NotificationType.ASSIGN_SUPPLIER_TO_SHOW,
        NotificationType.DELETE_SUPPLIER_FROM_SHOW,
      ];

      for (const key of keys) {
        apiV2.notification.subscribe({
          key,
          listenerId: `show_by_supplier_store_${key}`,
          callback: () => fetchBySupplier(params),
        });
      }
    },

    fetchBySupplier: async (params) => {
      if (!isSupplier()) {
        return;
      }
      try {
        const showsBySupplier = await apiV2.show.getBySupplier({});
        set({ itemsBySupplier: showsBySupplier });
      } catch (e) {
        console.error("Failed to fetch shows by supplier:", e);
        set({ itemsBySupplier: [] });
      }
    },

    autoCatchOne: (showId) => {
      const { fetchOne } = get();
      apiV2.notification.subscribe<
        INotificationBaseRelatedToShow<IPayloadUpdatedShow>
      >({
        key: NotificationType.UPDATED_SHOW,
        showId,
        listenerId: "show_one_store",
        callback: async ({ payload }) => {
          if (payload?.showId !== showId && !get().selected) return;
          await fetchOne({ showId }, true);
        },
      });

      return () =>
        apiV2.notification.unsubscribe({
          key: NotificationType.UPDATED_SHOW,
          showId,
          listenerId: "show_one_store",
        });
    },

    resetOne: () => {
      set({ selected: null, isLoadingOne: false });
    },

    update: async (body) => {
      try {
        const res = await apiV2.show.update(body);
        set((state) => ({
          error: null,
        }));
      } catch (e) {
        console.error("Failed to update show:", e);
        const message = getErrorMessage(e);
        set({ error: message });
        throw message;
      }
    },

    fetchBySupplierCompany: async (params) => {
      try {
        const { supplierCompanyId } = params;
        const showsByOrganiser = await apiV2.show.getBySupplierCompany({
          supplierCompanyId,
        });
        set({ itemsBySupplierCompany: showsByOrganiser });
      } catch (e) {
        console.error("Failed to fetch shows by supplierCompany:", e);
        set({ itemsBySupplierCompany: [] });
      }
    },
  }))
);

registerStore(useShows);
